import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import { BackListButton, Loading } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import Variant from '@/metrics/components/Variant/Variant'
import VariantMetrics from '@/metrics/components/VariantMetrics/VariantMetrics'
import { groupByValues } from '@/metrics/constants'
import { getTitleByOrdering } from '@/metrics/helpers/helpers'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import * as Styles from './styles'

const VariantByCategoryMetricsDetailPage = ({ isUnifiedDashboard }) => {
  const { categoryPosition, productPosition, variantPosition } = useParams()
  const { info } = useContext(AppContext)
  const { t } = useTranslation()
  const { regions } = useRegions()

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated
  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  } = useFilters()

  const { data: categories, loading: loadingCategory } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReturnedProducts,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: { groupBy: groupByValues.categories, ordering: productsFilters?.ordering },
    doRequest: !!categoryPosition,
  })

  const category = categories?.results?.[categoryPosition]

  const { data: topReturnedProducts, loading: loadingTopReturnedProducts } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getCategoryTopReturnedProducts,
    entityId: category?.id,
    entityType: entityTypes.category,
    filters: commonFilters,
    additionalFilters: { ordering: productsFilters?.ordering },
    doRequest: !!category?.id,
  })

  const product = topReturnedProducts?.results?.[productPosition]

  const { data: topVariantsData, loading: loadingTopVariants } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getProductTopVariants,
    entityId: product?.id,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: {
      categoryId: category?.id,
    },
    doRequest: !!product?.id,
  })

  const categoryPath = isUnifiedDashboard
    ? routes.merchantDashboardCategoryMetricsDetailPage
    : routes.categoryMetricsDetailPage
  const productPath = isUnifiedDashboard
    ? routes.merchantDashboardProductByCategoryMetricsDetailPage
    : routes.productByCategoryMetricsDetailPage
  const dashboardPath = isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage

  const getDashboardUrl = () => {
    return `${dashboardPath}?${getUrlSearchParams()}`
  }

  const getBreadcrumbs = () => {
    return [
      {
        title: `#${Number(categoryPosition) + 1} ${t('Categoría')}`,
        subtitle: category?.name,
        url: `${categoryPath.replace(':categoryPosition', categoryPosition)}?${getUrlSearchParams()}`,
      },
      {
        title: `#${Number(productPosition) + 1} ${t('Producto')}`,
        subtitle: topReturnedProducts?.results?.[productPosition]?.name,
        url: `${productPath.replace(':categoryPosition', categoryPosition).replace(':productPosition', productPosition)}?${getUrlSearchParams()}`,
      },
      {
        title: `#${Number(variantPosition) + 1} ${t('Variante')}`,
        subtitle:
          topVariantsData?.results?.[variantPosition]?.name || topVariantsData?.results?.[variantPosition]?.title,
      },
    ]
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getDashboardUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={TShirtIcon}
        title={getTitleByOrdering('Categorías', productsFilters?.ordering, t)}
        loading={loadingCategory || loadingTopReturnedProducts || loadingTopVariants}
        item={
          topVariantsData?.results?.[variantPosition] && (
            <Variant
              variant={{ ...topVariantsData?.results?.[variantPosition], index: Number(variantPosition) + 1 }}
              showAmounts={false}
            />
          )
        }
        count={topVariantsData?.results?.[variantPosition]?.count}
        percentage={topVariantsData?.results?.[variantPosition]?.percentage}
        percentageText={t('producto')}
        breadcrumbs={getBreadcrumbs()}
        sales={topVariantsData?.results?.[variantPosition]?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
      />

      {topVariantsData?.results?.[variantPosition] && (
        <Loading show={loadingCategory || loadingTopReturnedProducts} height="350px">
          <VariantMetrics
            variantId={topVariantsData?.results?.[variantPosition]?.variant_id}
            returnPortalId={isUnifiedDashboard ? null : info?.selectedReturnPortal?.id}
            commonFilters={commonFilters}
            returnSalesRatioFilters={returnSalesRatioFilters}
            setReturnSalesRatioFilters={setReturnSalesRatioFilters}
            returnsAndExchangesRatioFilters={returnsAndExchangesRatioFilters}
            setReturnsAndExchangesRatioFilters={setReturnsAndExchangesRatioFilters}
            storeCurrency={info?.selectedStore?.currency}
            categoryId={category?.id}
            salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
          />
        </Loading>
      )}
    </Styles.Container>
  )
}

export default VariantByCategoryMetricsDetailPage
